import React, { useEffect, useMemo, useState } from "react";
import { Search, Download } from "lucide-react";
import "../css/OrderReviews.css";

import { fetchOrderReviews as apiFetchOrderReviews } from "../service/api/orders";


const ExportIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);
const ResetIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
    </svg>
);

export default function OrderReviews() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");


    const [ratingType, setRatingType] = useState("all");
    const [rating, setRating] = useState("");

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        fetchOrderReviews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, debouncedSearch, ratingType, rating]);

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(total / perPage)),
        [total, perPage]
    );

    const from = total === 0 ? 0 : (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, total);

    const fetchOrderReviews = async () => {
        try {
            setLoading(true);

            const result = await apiFetchOrderReviews({
                page,
                per_page: perPage,
                search: debouncedSearch,

                rating_type: ratingType,
                rating,
            });

            const payload = result?.data?.data ? result.data : result;

            setRows(payload?.data ?? []);
            setTotal(payload?.total ?? 0);
        } catch (error) {
            console.error("Order reviews fetch failed:", error);
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = () => {
        setSearch("");
        setDebouncedSearch("");
        setRatingType("all");
        setRating("");
        setPage(1);
        setPerPage(10);
    };


    const exportCsv = () => {
        const headers = [
            "Order ID", "Customer Name", "Store Name", "Store Rating",
            "Delivery Boy", "Delivery Rating", "Order Total", "Order Placed At",
        ];

        const csvRows = rows.map((item) => [
            item.unique_order_id ?? item.order_id ?? "-",
            item.customer_name ?? item.user?.name ?? "-",
            item.store_name ?? item.restaurant?.name ?? "-",
            item.store_rating ?? item.rating_store ?? "-",
            item.delivery_boy ?? item.delivery?.name ?? item.accept_delivery?.user?.name ?? "-",
            item.delivery_rating ?? item.rating_delivery ?? "-",
            item.order_total ?? item.total ?? "-",
            item.order_placed_at ?? item.created_at ?? "-",
        ]);

        const csvContent = [
            headers.join(","),
            ...csvRows.map((row) =>
                row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")
            ),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "order-reviews.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        if (page > 4) pages.push("...");
        const start = Math.max(2, page - 1);
        const end = Math.min(totalPages - 1, page + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        if (page < totalPages - 3) pages.push("...");
        pages.push(totalPages);
        return pages;
    };

    return (
        <div className="or-page">
            {/* ── Header ── */}
            <div className="page-header">
                <div>
                    <p className="page-header__eyebrow">Management</p>
                    <h1 className="page-header__title">Order Reviews</h1>
                </div>
                <div className="page-header__actions">
                    <button className="btn" onClick={exportCsv}><ExportIcon/> Export CSV</button>
                    <button className="btn" onClick={resetFilters}
                            style={{color: "#ef4444", borderColor: "#fca5a5", background: "#fef2f2"}}>
                        <ResetIcon/> Reset Filters
                    </button>
                </div>
            </div>

            {/* ── Card ── */}
            <div className="or-card">

                {/* Toolbar */}
                <div className="or-toolbar">
                    <div className="or-search-wrap">
        <span className="or-search-icon">
            <Search size={15}/>
        </span>
                        <input
                            className="or-search-input"
                            placeholder="Search order / customer / store..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="or-right-actions">
                        <select
                            className="or-filter-select"
                            value={ratingType}
                            onChange={(e) => {
                                setRatingType(e.target.value);
                                setPage(1);
                            }}
                        >
                            <option value="all">All Ratings</option>
                            <option value="store">Store Rating</option>
                            <option value="delivery">Delivery Rating</option>
                        </select>

                        <select
                            className="or-filter-select"
                            value={rating}
                            onChange={(e) => {
                                setRating(e.target.value);
                                setPage(1);
                            }}
                        >
                            <option value="">Any Rating</option>
                            <option value="5">5 ★</option>
                            <option value="4">4 ★</option>
                            <option value="3">3 ★</option>
                            <option value="2">2 ★</option>
                            <option value="1">1 ★</option>
                            <option value="low">Low ≤ 2</option>
                        </select>

                        <select
                            className="or-filter-select or-page-size-select"
                            value={perPage}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>

                {/* ── Desktop table ── */}
                <div className="or-table-wrap">
                    <table className="or-table">
                        <thead className="or-thead">
                        <tr>
                            <Th label="Order ID"/>
                            <Th label="Customer"/>
                            <Th label="Store"/>
                            <Th label="Store Rating"/>
                            <Th label="Delivery Boy" extraClass="or-hide-md"/>
                            <Th label="Delivery Rating"/>
                            <Th label="Order Total"/>
                            <Th label="Placed At"/>
                        </tr>
                        </thead>

                        <tbody className="or-tbody">
                        {loading ? (
                            Array.from({length: perPage > 10 ? 10 : perPage}).map((_, i) => (
                                <tr key={i}>
                                    {Array.from({length: 8}).map((__, j) => (
                                        <td key={j} className="or-skeleton-td">
                                            <div className="or-skeleton"
                                                 style={{width: j === 0 ? 70 : j === 7 ? 110 : "80%"}}/>
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={8}>
                                    <div className="or-empty">
                                        <div className="or-empty__icon">⭐</div>
                                        <div className="or-empty__title">No order reviews found</div>
                                        <div className="or-empty__sub">Try adjusting your search or filters</div>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            rows.map((item, index) => (
                                <tr key={item.id ?? item.order_id ?? index}>
                                    <td className="or-td">
                                        <a
                                            className="or-order-id"
                                            href={`https://dashboard.totalfresh.co/order/${item.unique_order_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={item.unique_order_id}
                                        >
                                            # {item.unique_order_id
                                            ? item.unique_order_id.substring(item.unique_order_id.length - 7)
                                            : item.order_id ?? "-"}
                                        </a>
                                    </td>
                                    <td className="or-td">
                                            <span className="or-name">
                                                {item.customer_name ?? item.user?.name ?? "-"}
                                            </span>
                                    </td>
                                    <td className="or-td">
                                            <span className="or-store-name">
                                                {item.store_name ?? item.restaurant?.name ?? "-"}
                                            </span>
                                    </td>
                                    <td className="or-td">
                                        <RatingBadge value={item.store_rating ?? item.rating_store}/>
                                    </td>
                                    <td className="or-td or-hide-md">
                                            <span className="or-name">
                                                {item.delivery_boy ?? item.delivery?.name ?? item.accept_delivery?.user?.name ?? "-"}
                                            </span>
                                    </td>
                                    <td className="or-td">
                                        <RatingBadge value={item.delivery_rating ?? item.rating_delivery}/>
                                    </td>
                                    <td className="or-td">
                                            <span className="or-total">
                                                {formatAmount(item.order_total ?? item.total)}
                                            </span>
                                    </td>
                                    <td className="or-td">
                                            <span className="or-date">
                                                {formatDate(item.order_placed_at ?? item.created_at)}
                                            </span>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

                {/* ── Mobile cards ── */}
                <div className="or-card-list">
                    {loading ? (
                        Array.from({length: 5}).map((_, i) => (
                            <div key={i} className="or-card-row">
                                <div className="or-skeleton" style={{width: "40%", marginBottom: 10}}/>
                                <div className="or-card-row__meta">
                                    {Array.from({length: 4}).map((__, j) => (
                                        <div key={j} className="or-card-meta-item">
                                            <div className="or-skeleton" style={{width: "50%", height: 10}}/>
                                            <div className="or-skeleton" style={{width: "70%", height: 14}}/>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : rows.length === 0 ? (
                        <div className="or-empty">
                            <div className="or-empty__icon">⭐</div>
                            <div className="or-empty__title">No order reviews found</div>
                            <div className="or-empty__sub">Try adjusting your search or filters</div>
                        </div>
                    ) : (
                        rows.map((item, index) => (
                            <div key={item.id ?? item.order_id ?? index} className="or-card-row">
                                <div className="or-card-row__top">
                                    <span className="or-order-id">
                                        #{item.unique_order_id ?? item.order_id ?? "-"}
                                    </span>
                                    <RatingBadge value={item.store_rating ?? item.rating_store}/>
                                </div>

                                <div className="or-card-row__meta">
                                    <div className="or-card-meta-item">
                                        <span className="or-card-meta-label">Customer</span>
                                        <span className="or-card-meta-value">
                                            {item.customer_name ?? item.user?.name ?? "-"}
                                        </span>
                                    </div>
                                    <div className="or-card-meta-item">
                                        <span className="or-card-meta-label">Store</span>
                                        <span className="or-card-meta-value">
                                            {item.store_name ?? item.restaurant?.name ?? "-"}
                                        </span>
                                    </div>
                                    <div className="or-card-meta-item">
                                        <span className="or-card-meta-label">Delivery Boy</span>
                                        <span className="or-card-meta-value">
                                            {item.delivery_boy ?? item.delivery?.name ?? item.accept_delivery?.user?.name ?? "-"}
                                        </span>
                                    </div>
                                    <div className="or-card-meta-item">
                                        <span className="or-card-meta-label">Delivery Rating</span>
                                        <RatingBadge value={item.delivery_rating ?? item.rating_delivery}/>
                                    </div>
                                </div>

                                <div className="or-card-row__footer">
                                    <span className="or-card-date">
                                        {formatDate(item.order_placed_at ?? item.created_at)}
                                    </span>
                                    <span className="or-total">
                                        {formatAmount(item.order_total ?? item.total)}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* ── Pagination ── */}
                <div className="or-pagination">
                    <span className="or-pagination__info">
                        Showing {from}–{to} of {total.toLocaleString()} entries
                    </span>

                    <div className="or-pagination__pages">
                        <button
                            className="or-page-btn or-page-btn--nav"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            ←
                        </button>

                        {getPageNumbers().map((item, index) =>
                            item === "..." ? (
                                <span key={`dots-${index}`} className="or-page-ellipsis">…</span>
                            ) : (
                                <button
                                    key={item}
                                    className={`or-page-btn${page === item ? " or-page-btn--active" : ""}`}
                                    onClick={() => setPage(item)}
                                >
                                    {item}
                                </button>
                            )
                        )}

                        <button
                            className="or-page-btn or-page-btn--nav"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Sub-components ──────────────────────────────────────────────────────── */

function Th({label, extraClass = ""}) {
    return (
        <th className={`or-th${extraClass ? ` ${extraClass}` : ""}`}>
            <div className="or-th-content">
                <span>{label}</span>
            </div>
        </th>
    );
}

function RatingBadge({value}) {
    if (value === null || value === undefined || value === "") {
        return <span className="or-rating or-rating--none">—</span>;
    }
    const n = Number(value);
    if (Number.isNaN(n)) return <span className="or-rating or-rating--none">—</span>;

    const cls = n >= 4 ? "or-rating--high" : n >= 3 ? "or-rating--mid" : "or-rating--low";
    return <span className={`or-rating ${cls}`}>★ {n.toFixed(1)}</span>;
}

/* ── Formatters ──────────────────────────────────────────────────────────── */

function formatAmount(value) {
    if (value === null || value === undefined || value === "") return "-";
    const n = Number(value);
    if (Number.isNaN(n)) return value;
    return "₹" + n.toLocaleString("en-IN", {maximumFractionDigits: 2});
}

function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
}